/* ============================================================================
   VALINO — DYNAMIC PRODUCT GRIDS
   ----------------------------------------------------------------------------
   Keeps homepage product rails alive. Every rotating grid cycles through fresh
   slices of its own collection so a shopper who visited yesterday never lands
   on the same eight products today.

   How it works
   ------------
   Each rotating section renders normally (page 1) on first paint. This script
   then pulls further pages through Shopify's Section Rendering API:

       /?section_id=<section.id>&page=<n>

   Because the markup comes back from the very same Liquid section, every card
   is pixel-identical to a server-rendered one — same aspect ratio, same badges,
   same working quick-add form. Nothing is reconstructed client-side, so there
   is no way for a card to point at the wrong product.

   Rotation is deliberately polite:
     · rails refresh *before* they reach the screen, so a shopper scrolling
       down arrives at products they have not seen rather than watching them
       change; only the rail already in view animates its first refresh
     · after that first refresh it pauses on hover, focus and touch, and
       while any modal/drawer is open
     · pauses when the tab is hidden
     · honours prefers-reduced-motion (one fresh set, then it settles)
     · the next set is prefetched and its images decoded *before* the swap,
       so tiles never flash empty
     · if a fetch fails the grid simply keeps what it has

   Owned by Valino. Safe to disable per-section from the theme editor
   ("Dynamic product rotation" → uncheck "Rotate products automatically").
   ========================================================================== */
(function () {
  'use strict';

  if (window.__valinoDynamicGrid) return;
  window.__valinoDynamicGrid = true;

  var SUPPORTED =
    'IntersectionObserver' in window &&
    typeof window.fetch === 'function' &&
    typeof window.DOMParser === 'function' &&
    typeof Promise !== 'undefined';

  if (!SUPPORTED) return;

  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var FIRST_ROTATION_MS = 900; // brings the first refresh forward (see prefetchNext)
  var FADE_OUT_MS = 220; // must stay in sync with valino-dynamic-grid.css
  var FADE_IN_MS = 320;
  var STAGGER_MS = 45; // per-tile offset, gives the "wave" feel
  var IMAGE_WAIT_CAP_MS = 1500; // never block a swap longer than this
  var RESUME_AFTER_TOUCH_MS = 9000;
  var MAX_PARALLEL_FETCHES = 2;

  /* ---------------------------------------------------------------------
     Tiny fetch queue — keeps a page full of rails from opening a dozen
     connections at once when the shopper scrolls fast.
     --------------------------------------------------------------------- */
  var inFlight = 0;
  var waiting = [];

  function drainQueue() {
    while (inFlight < MAX_PARALLEL_FETCHES && waiting.length) {
      var job = waiting.shift();
      inFlight++;
      job();
    }
  }

  function queuedFetch(url) {
    return new Promise(function (resolve, reject) {
      waiting.push(function () {
        fetch(url, { credentials: 'same-origin' })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.text();
          })
          .then(resolve, reject)
          .then(function () {
            inFlight--;
            drainQueue();
          });
      });
      drainQueue();
    });
  }

  /* =====================================================================
     MEMORY — what this shopper has already been shown
     ---------------------------------------------------------------------
     This is what makes the rails feel considered rather than random. Every
     time a rail shows a set of products, the page number is written to
     localStorage against that rail. On the next visit — tomorrow, next week —
     those sets are skipped, so the shopper keeps meeting parts of the
     catalogue they have not seen yet instead of the same faces.

     When a rail has been exhausted the memory for it resets and the cycle
     begins again, and the whole record expires after MEMORY_TTL_DAYS so a
     catalogue that has changed gets a fresh airing.

     Costs nothing: no extra requests, no server state, no cookies. Fails
     silently in private mode or when storage is full.
     ===================================================================== */
  var MEMORY_KEY = 'valino:rails:v1';
  var MEMORY_TTL_DAYS = 21;

  var Memory = {
    _cache: null,

    load: function () {
      if (this._cache) return this._cache;
      var data = {};
      try {
        var raw = window.localStorage.getItem(MEMORY_KEY);
        if (raw) {
          var parsed = JSON.parse(raw);
          var age = (Date.now() - (parsed.ts || 0)) / 86400000;
          if (age < MEMORY_TTL_DAYS && parsed.rails) data = parsed.rails;
        }
      } catch (e) {
        /* private mode, disabled storage, corrupt value — carry on blind */
      }
      this._cache = data;
      return data;
    },

    seenPages: function (railKey) {
      var d = this.load();
      return d[railKey] || [];
    },

    remember: function (railKey, page) {
      var d = this.load();
      var list = d[railKey] || [];
      if (list.indexOf(page) === -1) list.push(page);
      d[railKey] = list;
      try {
        window.localStorage.setItem(
          MEMORY_KEY,
          JSON.stringify({ ts: Date.now(), rails: d })
        );
      } catch (e) {
        /* storage full — the in-memory copy still guides this session */
      }
    },

    /* Every set has been shown: wipe this rail so it can cycle again. */
    reset: function (railKey) {
      var d = this.load();
      delete d[railKey];
      try {
        window.localStorage.setItem(
          MEMORY_KEY,
          JSON.stringify({ ts: Date.now(), rails: d })
        );
      } catch (e) {}
    },
  };

  /* --------------------------------------------------------------------- */

  function shuffle(list) {
    var a = list.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i];
      a[i] = a[j];
      a[j] = t;
    }
    return a;
  }

  function delay(ms) {
    return new Promise(function (r) {
      setTimeout(r, ms);
    });
  }

  /* Resolves once every image is loaded (or errored), capped so a slow CDN
     can never stall the rotation. */
  function imagesSettled(scope, capMs) {
    var imgs = Array.prototype.slice.call(scope.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();

    var loads = imgs.map(function (img) {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(function (resolve) {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    });

    return Promise.race([Promise.all(loads), delay(capMs)]);
  }

  /* Dawn's scroll-reveal hides an element with `opacity: 0.01` and only
     animates it back to full opacity while it lacks `.scroll-trigger--offscreen`.
     That is a race we do not want to depend on for a rotated-in card: if the
     theme's observer ever tags one as offscreen, it stays invisible and the
     shopper sees a hole in the grid. There is also an animation-delay of up to
     600ms during which the card is blank.

     Rotated cards are already faded in by this script's own crossfade, so the
     theme's reveal is redundant here. Stripping it makes visibility
     deterministic. The server-rendered first page is untouched and keeps the
     theme's reveal exactly as before. */
  var REVEAL_CLASSES = [
    'scroll-trigger',
    'animate--slide-in',
    'animate--fade-in',
    'scroll-trigger--offscreen',
    'scroll-trigger--cancel',
  ];

  function stripScrollReveal(root) {
    var targets = [root].concat(Array.prototype.slice.call(root.querySelectorAll('.scroll-trigger')));
    targets.forEach(function (el) {
      REVEAL_CLASSES.forEach(function (c) {
        el.classList.remove(c);
      });
      el.removeAttribute('data-cascade');
      el.style.removeProperty('--animation-order');
    });
  }

  /* Anything that means "the shopper is busy — do not move the furniture". */
  function pageIsBusy() {
    if (document.hidden) return true;
    var body = document.body;
    if (body.classList.contains('overflow-hidden')) return true; // Dawn body lock
    if (body.classList.contains('overflow-hidden-tablet')) return true;
    if (body.classList.contains('overflow-hidden-mobile')) return true;
    if (document.querySelector('.modal__content[open], quick-add-modal[open]')) return true;
    if (document.querySelector('cart-drawer.animate.active, .valino-modal.is-open, #valino-quick-view.is-open')) {
      return true;
    }
    return false;
  }

  /* =====================================================================
     Rotator — one per rotating section
     ===================================================================== */
  function Rotator(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-rotate-section-id');
    this.grid = document.getElementById(root.getAttribute('data-rotate-target'));
    this.interval = parseInt(root.getAttribute('data-rotate-interval'), 10) || 5000;
    this.totalPages = parseInt(root.getAttribute('data-rotate-total-pages'), 10) || 1;

    // Stable across theme pushes: 'template--123__womens_picks' -> 'womens_picks'
    this.railKey = (this.sectionId || 'rail').split('__').pop();
    this.currentPage = 1;
    this.queue = [];
    this.staging = null;
    this.timer = null;
    this.armed = false;
    this.hovering = false;
    this.touchedUntil = 0;
    this.swapping = false;
    this.dead = false;
    this.rotations = 0;

    if (!this.grid || this.totalPages < 2) {
      this.dead = true;
      return;
    }

    // Dawn's product grids number their tiles Slide-<section>-<n> and
    // <slider-component> relies on those ids. Custom rails (the summer edit,
    // for instance) don't use them — detect rather than assume.
    var first = this.grid.firstElementChild;
    this.usesSlideIds = !!(first && first.id && first.id.indexOf('Slide-') === 0);

    // A rotation must never shrink the rail. Liquid already floors away the
    // trailing partial page, but a collection edited between page load and
    // fetch could still hand back a short set — those pages get dropped.
    this.expectedCount = this.grid.children.length;
    this.excluded = {};

    this.bind();
  }

  Rotator.prototype.bind = function () {
    var self = this;

    // Hover / focus — never yank a card out from under a shopper.
    this.root.addEventListener('mouseenter', function () {
      self.hovering = true;
    });
    this.root.addEventListener('mouseleave', function () {
      self.hovering = false;
    });
    this.root.addEventListener('focusin', function () {
      self.hovering = true;
    });
    this.root.addEventListener('focusout', function () {
      if (!self.root.contains(document.activeElement)) self.hovering = false;
    });

    // Touch / manual swipe on the mobile slider — back off for a while.
    var touched = function () {
      self.touchedUntil = Date.now() + RESUME_AFTER_TOUCH_MS;
    };
    this.root.addEventListener('touchstart', touched, { passive: true });
    if (this.grid) this.grid.addEventListener('scroll', touched, { passive: true });

    // Two observers, because "should this rail be working" and "can the
    // shopper actually see it right now" are different questions.
    //
    // ARMED: a very generous margin so a rail starts fetching and swaps in a
    // fresh set long *before* it reaches the screen. This is the whole point —
    // by the time the shopper scrolls down, the products are already new, so
    // they never meet the same line-up they saw last visit.
    this.observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            self.armed = true;
            self.start();
          } else {
            self.armed = false;
            self.stop();
          }
        }
      },
      { rootMargin: '1600px 0px', threshold: 0 }
    );
    this.observer.observe(this.root);

    // ON SCREEN: used only to decide whether a swap needs to be gentle
    // (visible, so animate it) or can happen instantly (off screen, nobody
    // is looking).
    this.onScreen = false;
    this.screenObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) self.onScreen = entries[i].isIntersecting;
      },
      { rootMargin: '0px', threshold: 0.05 }
    );
    this.screenObserver.observe(this.root);
  };

  Rotator.prototype.start = function () {
    if (this.dead || this.timer) return;
    var self = this;
    this.timer = setInterval(function () {
      self.tick();
    }, this.interval);
    // Warm the first replacement set straight away so the very first swap is
    // instant rather than waiting on the network.
    this.prefetchNext();
  };

  Rotator.prototype.stop = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    // The pending FIRST refresh is deliberately left running. A rail arms
    // 1600px before it is reached, so a shopper moving at any speed scrolls
    // out of the armed zone while that first set is still downloading —
    // cancelling it here threw the finished set away and left the rail
    // showing the server's page 1 for the entire visit. Only a dead rail
    // (three failed fetches) abandons it.
    if (this.dead && this.firstRunTimer) {
      clearTimeout(this.firstRunTimer);
      this.firstRunTimer = null;
    }
  };

  Rotator.prototype.nextPage = function () {
    if (!this.queue.length) {
      var available = [];
      var p;
      for (p = 1; p <= this.totalPages; p++) {
        if (p !== this.currentPage && !this.excluded[p]) available.push(p);
      }
      if (!available.length) return null;

      // Prefer sets this shopper has never been shown — across visits, not
      // just this page load. That is the difference between "the products
      // move" and "I keep seeing something new".
      var seen = Memory.seenPages(this.railKey);
      var unseen = available.filter(function (n) {
        return seen.indexOf(n) === -1;
      });

      if (!unseen.length) {
        // Whole rail exhausted — start the catalogue over rather than
        // stalling on the sets they already know.
        Memory.reset(this.railKey);
        unseen = available;
      }

      // Shuffled rather than sequential: two shoppers landing on the same
      // rail at the same moment should not walk the catalogue in lockstep.
      this.queue = shuffle(unseen);
    }
    return this.queue.shift();
  };

  Rotator.prototype.tick = function () {
    if (this.dead || this.swapping) return;

    // Normally a rail only rotates while armed. The one exception is a rail
    // that has never refreshed yet and already has its replacement set
    // downloaded and parked in staging: that swap must land. It is what
    // guarantees a returning shopper does not meet the same line-up, and
    // off screen it is free — the swap below runs instantly with no
    // crossfade, no layout change and nobody watching.
    var guaranteedFirst = this.rotations === 0 && !!this.pendingPage;
    if (!this.armed && !guaranteedFirst) return;

    if (pageIsBusy()) {
      // A hidden tab or an open drawer must not silently consume the one
      // guaranteed refresh — with the interval stopped, nothing would come
      // back for it. Try again shortly instead of dropping it.
      if (guaranteedFirst && !this.firstRunTimer) {
        var again = this;
        this.firstRunTimer = setTimeout(function () {
          again.firstRunTimer = null;
          again.tick();
        }, 1000);
      }
      return;
    }

    // The FIRST refresh is the one that matters: it is what stops a returning
    // shopper meeting the same line-up they saw last time. A resting cursor or
    // a finger that touched the page must not be able to freeze a rail on the
    // stale set forever, so those two only defer later rotations.
    if (this.rotations > 0) {
      if (this.hovering) return;
      if (Date.now() < this.touchedUntil) return;
    }

    // Reduced motion: give one fresh set on first view, then leave it alone.
    if (REDUCED_MOTION && this.rotations >= 1) {
      this.dead = true;
      this.stop();
      return;
    }

    this.swap();
  };

  /* Builds (once) an off-screen clone of the grid used purely to download and
     decode the incoming cards at the correct rendered width, so srcset picks
     the same image the live grid would. */
  Rotator.prototype.getStaging = function () {
    if (this.staging) return this.staging;

    // Mirror the live container exactly (tag + classes) so the staged cards
    // lay out at the same width and srcset resolves to the same image file.
    var ul = document.createElement(this.grid.tagName);
    ul.className = this.grid.className + ' valino-rot-staging';
    ul.setAttribute('aria-hidden', 'true');
    ul.setAttribute('role', 'presentation');
    // `inert` keeps the parked cards out of the tab order and off the a11y
    // tree. It is inherited, so it lifts automatically the moment a card is
    // moved into the live grid.
    if ('inert' in HTMLElement.prototype) ul.inert = true;
    this.grid.parentNode.insertBefore(ul, this.grid);
    this.staging = ul;
    return ul;
  };

  /* Fetches page N and leaves its <li>s parked in the staging grid. */
  Rotator.prototype.fetchPage = function (page) {
    var self = this;
    var url =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(this.sectionId) +
      '&page=' +
      page;

    return queuedFetch(url).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var incoming = doc.getElementById(self.grid.id);
      if (!incoming) throw new Error('grid not found in section response');

      var items = Array.prototype.slice.call(incoming.children);
      if (items.length < self.expectedCount) {
        self.excluded[page] = true;
        throw new Error('page ' + page + ' is short — skipping it');
      }

      var staging = self.getStaging();
      // The staging grid is out of flow, so it has no width of its own to
      // inherit. Pin it to the live grid's current width so the browser
      // picks exactly the same srcset candidate — the image is then already
      // in cache when the card goes live.
      staging.style.width = self.grid.getBoundingClientRect().width + 'px';
      staging.innerHTML = '';
      items.forEach(function (li) {
        var node = document.importNode(li, true);
        // Drop the Slide-* id while parked: <slider-component> finds its
        // slides with [id^="Slide-"] and must not see the staged copies.
        // The id is restored when the card moves into the live grid.
        node.removeAttribute('id');
        stripScrollReveal(node);
        // Force-load inside the staging grid; these images must be ready
        // *before* they ever become visible.
        Array.prototype.forEach.call(node.querySelectorAll('img'), function (img) {
          img.setAttribute('loading', 'eager');
        });
        staging.appendChild(node);
      });

      return imagesSettled(staging, IMAGE_WAIT_CAP_MS).then(function () {
        return page;
      });
    });
  };

  Rotator.prototype.prefetchNext = function () {
    // `pendingPage` means a set is already downloaded and waiting in staging.
    // Without this guard, every re-entry into view re-fetched a *different*
    // page and threw the ready one away — roughly doubling the requests for
    // no benefit. These responses carry no cache-control, so a discarded
    // fetch is a wasted round trip to the origin every time.
    if (this.dead || this.pending || this.pendingPage) return;
    var self = this;
    var page = this.nextPage();
    if (!page) return;

    this.pending = this.fetchPage(page)
      .then(function () {
        self.pendingPage = page;
        // The server always renders page 1, and Shopify's full-page cache
        // means every visitor gets that identical first paint. So the first
        // refresh is brought as far forward as possible:
        //
        //   · rail still below the fold -> swap immediately, silently. The
        //     shopper scrolls down and simply arrives at products they have
        //     not seen, never witnessing a change.
        //   · rail already on screen    -> short delay, then animate, so it
        //     reads as a deliberate refresh rather than a glitch.
        if (!self.rotations && !self.firstRunTimer) {
          self.firstRunTimer = setTimeout(
            function () {
              self.firstRunTimer = null;
              self.tick();
            },
            self.onScreen ? FIRST_ROTATION_MS : 0
          );
        }
      })
      .catch(function () {
        // Network hiccup or an unexpected response — drop this page silently
        // and let the next tick try a different one. The grid keeps whatever
        // it is already showing, so the shopper sees nothing broken.
        self.pendingPage = null;
        // A deliberately excluded (short) page is not a fault; only real
        // failures count towards giving up.
        if (!self.excluded[page]) {
          self.failures = (self.failures || 0) + 1;
          if (self.failures >= 3) {
            self.dead = true;
            self.stop();
          }
        }
      })
      .then(function () {
        self.pending = null;
      });
  };

  Rotator.prototype.swap = function () {
    var self = this;

    // Nothing staged yet? Warm it and let the next tick do the swap.
    if (!this.pendingPage) {
      this.prefetchNext();
      return;
    }

    var staged = Array.prototype.slice.call(this.staging.children);
    if (!staged.length) {
      this.pendingPage = null;
      return;
    }

    this.swapping = true;
    var grid = this.grid;
    var page = this.pendingPage;
    this.pendingPage = null;

    var outgoing = Array.prototype.slice.call(grid.children);
    // Fractional, not offsetHeight: a rounded integer height written back as
    // an inline style is itself a sub-pixel layout shift, repeated on every
    // rotation. Card rows are a fixed aspect ratio so in practice two product
    // sets measure the same and the height is left completely alone.
    var startHeight = grid.getBoundingClientRect().height;

    var doInsert = function () {
      grid.innerHTML = '';
      staged.forEach(function (node, i) {
        node.classList.add('valino-rot-enter');
        node.style.setProperty('--valino-rot-delay', i * STAGGER_MS + 'ms');
        grid.appendChild(node);
      });

      // Re-point ids so Dawn's slider bookkeeping stays coherent.
      if (self.usesSlideIds) {
        Array.prototype.forEach.call(grid.children, function (node, i) {
          node.id = 'Slide-' + self.sectionId + '-' + (i + 1);
        });
      }

      // Only intervene if the two sets genuinely differ in height. Anything
      // under a couple of pixels is measurement noise, and "correcting" it
      // would create the very layout shift this is meant to avoid.
      var endHeight = grid.getBoundingClientRect().height;
      var easingHeight = Math.abs(endHeight - startHeight) > 2;

      if (easingHeight) {
        grid.style.height = startHeight + 'px';
        // force reflow before transitioning to the new height
        void grid.offsetHeight;
        grid.classList.add('valino-rot-resizing');
        grid.style.height = endHeight + 'px';
      }

      requestAnimationFrame(function () {
        Array.prototype.forEach.call(grid.children, function (node) {
          node.classList.add('valino-rot-enter-active');
        });
      });

      self.resyncSlider();

      return delay(FADE_IN_MS + staged.length * STAGGER_MS).then(function () {
        if (easingHeight) {
          grid.classList.remove('valino-rot-resizing');
          grid.style.height = '';
        }
        Array.prototype.forEach.call(grid.children, function (node) {
          node.classList.remove('valino-rot-enter', 'valino-rot-enter-active');
          node.style.removeProperty('--valino-rot-delay');
        });
        self.currentPage = page;
        Memory.remember(self.railKey, page);
        self.rotations++;
        self.swapping = false;
        // Keep a set queued only while the rail is still in play. Once the
        // guaranteed first refresh has landed on a rail the shopper has
        // already scrolled past, pulling further pages it may never show
        // would be a wasted round trip to the origin.
        if (self.armed) self.prefetchNext();
      });
    };

    // No crossfade when nobody can see it: off-screen rails swap instantly so
    // they are already refreshed by the time the shopper scrolls down.
    if (REDUCED_MOTION || !this.onScreen) {
      doInsert();
      return;
    }

    outgoing.forEach(function (node, i) {
      node.style.setProperty('--valino-rot-delay', i * STAGGER_MS + 'ms');
      node.classList.add('valino-rot-leave');
    });

    delay(FADE_OUT_MS + outgoing.length * STAGGER_MS).then(doInsert);
  };

  /* Dawn's <slider-component> caches its slide list. After a swap, nudge it
     back into a consistent state instead of leaving stale counters behind. */
  Rotator.prototype.resyncSlider = function () {
    var slider = this.root.querySelector('slider-component');
    if (!slider) return;
    try {
      if (this.grid.scrollLeft) this.grid.scrollLeft = 0;
      slider.sliderItems = slider.querySelectorAll('[id^="Slide-"]');
      if (typeof slider.initPages === 'function') slider.initPages();
    } catch (e) {
      /* A slider that refuses to resync is not worth breaking the page over. */
    }
  };

  /* =====================================================================
     Boot
     ===================================================================== */
  function init() {
    var roots = document.querySelectorAll('[data-valino-rotate]');
    if (!roots.length) return;
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__valinoRotator) return;
      root.__valinoRotator = new Rotator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor / dynamic section loads.
  document.addEventListener('shopify:section:load', init);

  // Don't burn cycles while the tab is in the background.
  document.addEventListener('visibilitychange', function () {
    var roots = document.querySelectorAll('[data-valino-rotate]');
    Array.prototype.forEach.call(roots, function (root) {
      var r = root.__valinoRotator;
      if (!r || r.dead) return;
      if (document.hidden) r.stop();
      else if (r.armed) r.start();
    });
  });
})();
